/** @jsxImportSource @emotion/react */
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import { EraImportStatus, EraImportSummary, EraSkippedTransaction } from 'store/types'
import { formatEasternDateTime } from 'helpers/dates'

const Money = ({ v }: { v?: string | number }) => (
  <span>{v ? `$${Number(v).toFixed(2)}` : '$0.00'}</span>
)

const formatDate = (value?: string) => formatEasternDateTime(value, '—')

export const eraImportHeadline = (summary: EraImportSummary): { title: string; variant: string } => {
  switch (summary.status) {
    case EraImportStatus.DUPLICATE:
      return { title: 'Nothing imported — this file was already uploaded', variant: 'danger' }
    case EraImportStatus.PARTIALLY_IMPORTED:
      return { title: 'Partially imported — some payments were already uploaded', variant: 'warning' }
    case EraImportStatus.EMPTY:
      return { title: 'No payments found in this file', variant: 'warning' }
    case EraImportStatus.IMPORTED:
    default:
      return { title: 'ERA file uploaded', variant: 'success' }
  }
}

const PayerCell = ({ transaction }: { transaction: EraSkippedTransaction }) => (
  <>
    <div>{transaction.payerName || '—'}</div>
    {transaction.checkNumber && (
      <div className="text-muted" css={{ fontSize: 12 }}>
        Check / Trace {transaction.checkNumber}
      </div>
    )}
  </>
)

const SourceCell = ({ transaction }: { transaction: EraSkippedTransaction }) =>
  transaction.existingEraFilename ? (
    <>
      <div>{transaction.existingEraFilename}</div>
      <div className="text-muted" css={{ fontSize: 12 }}>
        {formatDate(transaction.existingEraCreatedAt)}
      </div>
    </>
  ) : (
    <>Repeated within this file</>
  )

/**
 * One row per skipped claim. An 835 pays by check rather than by patient, so the payer
 * and source repeat across the claims of a single payment - the payment-level totals are
 * in the counters above, and a biller reconciles by patient and claim.
 */
const SkippedClaimRows = ({ transaction }: { transaction: EraSkippedTransaction }) => {
  const details = transaction.claimDetails ?? []
  const undisclosed = transaction.claims - details.length

  // a payment carrying no claims would otherwise vanish from a claim-keyed list
  if (details.length === 0) {
    return (
      <tr>
        <td className="text-muted">No claims on this payment</td>
        <td>
          <PayerCell transaction={transaction} />
        </td>
        <td>
          <Money v={transaction.checkAmount} />
        </td>
        <td>—</td>
        <td>
          <SourceCell transaction={transaction} />
        </td>
      </tr>
    )
  }

  return (
    <>
      {details.map((c, i) => (
        <tr key={i}>
          <td>{c.patientName || '—'}</td>
          <td>
            <PayerCell transaction={transaction} />
          </td>
          <td>
            <Money v={c.paidAmount} />
          </td>
          <td>{c.claimNumber || '—'}</td>
          <td>
            <SourceCell transaction={transaction} />
          </td>
        </tr>
      ))}
      {undisclosed > 0 && (
        <tr>
          <td colSpan={5} className="text-muted">
            …and {undisclosed} more claim{undisclosed === 1 ? '' : 's'} on this payment
          </td>
        </tr>
      )}
    </>
  )
}

// What one file's import did: the headline, the counts, and every payment
// skipped as already uploaded. Shown on its own for a single upload, and as a
// file's details inside the bulk upload.
export const EraUploadSummaryContent: React.FC<{ summary: EraImportSummary }> = ({ summary }) => {
  const { variant } = eraImportHeadline(summary)
  const skipped = summary.skippedTransactions ?? []

  return (
    <>
      <div className={`alert alert-${variant} mb-3`}>
        <div css={{ fontWeight: 600 }}>{summary.filename}</div>
        {summary.status === EraImportStatus.DUPLICATE && summary.duplicateOfFilename && (
          <div className="mt-1">
            The same remittance was already uploaded as{' '}
            <strong>{summary.duplicateOfFilename}</strong> on{' '}
            {formatDate(summary.duplicateOfCreatedAt)}. Nothing was added to the database.
          </div>
        )}
        {summary.status === EraImportStatus.DUPLICATE && !summary.duplicateOfFilename && (
          <div className="mt-1">
            Every payment in this file is already in the system. Nothing was added to the
            database.
          </div>
        )}
        {summary.status === EraImportStatus.EMPTY && (
          <div className="mt-1">The file parsed successfully but contained no payments.</div>
        )}
      </div>

      <div className="d-flex" css={{ gap: 40 }}>
        <div>
          <div className="text-muted" css={{ fontSize: 13 }}>
            Imported
          </div>
          <div css={{ fontWeight: 600 }}>
            {summary.importedTransactions} payment{summary.importedTransactions === 1 ? '' : 's'} ·{' '}
            {summary.importedClaims} claim{summary.importedClaims === 1 ? '' : 's'}
          </div>
        </div>
        <div>
          <div className="text-muted" css={{ fontSize: 13 }}>
            Skipped as duplicate
          </div>
          <div css={{ fontWeight: 600 }}>
            {skipped.length} payment{skipped.length === 1 ? '' : 's'} · {summary.skippedClaims}{' '}
            claim{summary.skippedClaims === 1 ? '' : 's'}
          </div>
        </div>
      </div>

      {skipped.length > 0 && (
        <div className="table-responsive mt-3">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Payer</th>
                <th>Amount</th>
                <th>Claim Identifier</th>
                <th>Imported from</th>
              </tr>
            </thead>
            <tbody>
              {skipped.map((t, i) => (
                <SkippedClaimRows key={i} transaction={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

type Props = {
  summary: EraImportSummary | null
  onClose: () => void
}

export const EraUploadSummaryModal: React.FC<Props> = ({ summary, onClose }) => {
  if (!summary) return null

  const { title } = eraImportHeadline(summary)

  return (
    <Modal
      show
      onHide={onClose}
      size="xl"
      centered
      css={{
        '.modal-content': { borderRadius: 15 },
        '.modal-body': { padding: '20px 30px' },
      }}>
      <Modal.Header>
        <Modal.Title css={{ fontSize: 20 }}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <EraUploadSummaryContent summary={summary} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
